import { useEffect, useCallback, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import apiClient from '@/services/api';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useAnalyticsStore } from '@/stores/analyticsStore';
import { useAnalyticsHubDetail } from '@/hooks/useAnalytics';
import { formatCompactNumber, formatPercent } from '@/utils/formatNumber';
import { PLATFORMS } from '@/components/ui/PlatformSelect';
import { RetryImage } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { countCharacters, countWords } from '@/utils/characterCount';
import { extractHashtags } from '@/utils/hashtags';
import { AnalyticsHubDetail } from '@/types/analytics';
import { SnapshotChart } from './SnapshotChart';
import { Tooltip } from '@/components/ui/Tooltip';
import { toast } from '@/utils/toast';
import { isMetricSupported, getPlatformAnalytics, supportsVideoMetrics, supportsDistributionMetrics } from '@/constants/platformAnalytics';
import { lockBodyScroll } from '@/hooks/useBodyScrollLock';
import { useEscapeLayer } from '@/hooks/useEscapeLayer';
import { useDrawerFocus } from '@/hooks/useDrawerFocus';

/**
 * Format watch time in seconds to human-readable format
 */
const formatWatchTime = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

/**
 * MetricTile Component
 */
interface MetricTileProps {
  label: string;
  value: string | number;
  tooltip?: string;
}

const MetricTile = ({ label, value, tooltip }: MetricTileProps) => (
  <div className="bg-white rounded-lg border border-gray-200 p-3">
    <div className="text-xs text-gray-500 uppercase mb-1 flex items-center gap-1">
      {label}
      {tooltip && (
        <Tooltip content={tooltip} side="top">
          <span className="material-symbols-outlined text-[14px] text-gray-400 cursor-help">info</span>
        </Tooltip>
      )}
    </div>
    <div className="text-xl font-bold text-gray-900">{value}</div>
  </div>
);

type PostMedia = AnalyticsHubDetail['postMedia'][number];

/**
 * Get post type label based on media content
 */
function getPostTypeLabel(media: PostMedia[]): string {
  if (!media || media.length === 0) return 'Text';

  const hasVideo = media.some((m) => m.type === 'VIDEO');
  const hasPdf = media.some((m) => m.type === 'PDF');
  const imageCount = media.filter((m) => m.type === 'IMAGE').length;

  if (hasVideo) return 'Video';
  if (hasPdf) return 'PDF';
  if (imageCount > 1) return 'Carousel';
  if (imageCount === 1) return 'Image';
  return 'Media';
}

/**
 * Get post type icon (HappyIcons)
 */
function getPostTypeIcon(media: PostMedia[]): string {
  if (!media || media.length === 0) return ICONS.EDIT;

  const hasVideo = media.some((m) => m.type === 'VIDEO');
  const hasPdf = media.some((m) => m.type === 'PDF');
  const imageCount = media.filter((m) => m.type === 'IMAGE').length;

  if (hasVideo) return ICONS.VIDEO;
  if (hasPdf) return ICONS.FILE;
  if (imageCount > 1) return ICONS.GRID;
  if (imageCount === 1) return ICONS.IMAGE;
  return ICONS.FILE;
}

/**
 * AnalyticsDetailDrawer Component
 *
 * Slide-in drawer showing detailed analytics for a single published post
 */
export const AnalyticsDetailDrawer = () => {
  const { t } = useTranslation('analytics');
  const { formatRelativeDate, formatDateTime, formatDateOnly } = useTimezone();
  const selectedId = useAnalyticsStore((s) => s.selectedChannelInstanceId);
  const setSelectedId = useAnalyticsStore((s) => s.setSelectedChannelInstanceId);

  const { data, isLoading } = useAnalyticsHubDetail(selectedId);
  const isOpen = selectedId !== null;

  const [captionExpanded, setCaptionExpanded] = useState(false);
  const [refreshState, setRefreshState] = useState<'idle' | 'requesting' | 'waiting' | 'done'>('idle');
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close handler
  const handleClose = useCallback(() => {
    setSelectedId(null);
    setCaptionExpanded(false);
    setRefreshState('idle');
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, [setSelectedId]);

  // Refresh handler
  const handleRefresh = useCallback(async () => {
    if (!selectedId || refreshState !== 'idle') return;
    setRefreshState('requesting');
    try {
      const { data: result } = await apiClient.post(`/api/analytics/hub/${selectedId}/refresh`);
      if (result.success && result.data) {
        if (result.data.queued) {
          setRefreshState('waiting');
          // Timeout fallback if SSE doesn't arrive in 30s
          refreshTimeoutRef.current = setTimeout(() => {
            setRefreshState('idle');
            toast.warning(t('refresh_timeout', { defaultValue: 'Refresh is taking longer than expected. Check back soon.' }));
          }, 30000);
        } else {
          // Rate limited
          setRefreshState('idle');
          toast.warning(result.data.message);
        }
      }
    } catch {
      setRefreshState('idle');
      toast.error(t('refresh_failed', { defaultValue: 'Failed to refresh analytics' }));
    }
  }, [selectedId, refreshState, t]);

  // Track snapshot count to detect when SSE-triggered refresh arrives
  const prevSnapshotCount = useRef<number>(0);

  useEffect(() => {
    if (!data?.snapshots) return;
    const currentCount = data.snapshots.length;

    // If we're waiting for refresh and snapshot count increased, it arrived!
    if (refreshState === 'waiting' && currentCount > prevSnapshotCount.current && prevSnapshotCount.current > 0) {
      setRefreshState('idle');
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      toast.success(t('refresh_complete', { defaultValue: 'Analytics updated!' }));
    }

    prevSnapshotCount.current = currentCount;
  }, [data?.snapshots, refreshState, t]);

  // ESC (topmost layer only; inner popovers consume the key first)
  useEscapeLayer(isOpen, handleClose);
  const drawerRef = useRef<HTMLDivElement>(null);
  useDrawerFocus(isOpen, drawerRef);

  // Body scroll lock
  useEffect(() => {
    if (!isOpen) return;

    const unlockScroll = lockBodyScroll();

    return () => {
      unlockScroll();
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [isOpen, handleClose]);

  // Find platform info
  const platformInfo = PLATFORMS.find((p) => p.value === data?.platform);

  // Get platform analytics config
  const platform = data?.platform || '';
  const analyticsConfig = getPlatformAnalytics(platform);

  // Get latest snapshot for metrics
  const latestSnapshot = data?.snapshots && data.snapshots.length > 0
    ? data.snapshots[data.snapshots.length - 1]
    : null;

  // Check if refresh should be disabled (last snapshot < 6 hours ago)
  const isRefreshDisabled = (() => {
    if (!latestSnapshot) return false;
    const sixHoursMs = 6 * 60 * 60 * 1000;
    const timeSinceLastFetch = Date.now() - new Date(latestSnapshot.fetchedAt).getTime();
    return timeSinceLastFetch < sixHoursMs;
  })();

  // Check if all metrics are zero (collecting data state)
  const allMetricsZero = latestSnapshot
    ? latestSnapshot.reach === 0 &&
      latestSnapshot.impressions === 0 &&
      latestSnapshot.engagement === 0 &&
      latestSnapshot.likes === 0 &&
      latestSnapshot.comments === 0 &&
      latestSnapshot.shares === 0
    : true;

  // Caption stats
  const caption = data?.postCaption || '';
  const charCount = countCharacters(caption);
  const wordCount = countWords(caption);
  const hashtags = extractHashtags(caption);
  const shouldShowReadMore = caption.length > 150;

  // Post type
  const mediaList = data?.postMedia || [];
  const typeLabel = getPostTypeLabel(mediaList);
  const typeIcon = getPostTypeIcon(mediaList);

  return createPortal(
    <div
      className={`fixed inset-0 z-modal-backdrop transition-opacity duration-300 ease-in-out ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal={isOpen ? 'true' : undefined}
        aria-label={t('detail_drawer_label', { defaultValue: 'Post analytics details' })}
        className={`fixed right-0 top-0 bottom-0 w-full sm:w-96 lg:w-[420px] bg-white shadow-2xl z-modal-content flex flex-col transform transition-transform duration-300 ease-in-out outline-none ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {isLoading || !data ? (
          // Loading state
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
              <p className="text-gray-500">{t('loading_analytics', { defaultValue: 'Loading analytics...' })}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header (non-scrolling) */}
            <div className="bg-gray-50 border-b border-gray-200 px-5 sm:px-4 py-4 flex-shrink-0">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {/* Channel avatar with platform badge */}
                  <div className="relative flex-shrink-0 w-10 h-10">
                    {data.channelAvatarUrl ? (
                      <>
                        <img
                          src={data.channelAvatarUrl}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        {/* Platform badge overlay */}
                        {platformInfo && (
                          <span
                            className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center border-2 border-white"
                            style={{ backgroundColor: platformInfo.color }}
                          >
                            <FontAwesomeIcon
                              icon={platformInfo.icon}
                              className="text-white text-[8px]"
                            />
                          </span>
                        )}
                      </>
                    ) : platformInfo ? (
                      <span
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: platformInfo.color }}
                      >
                        <FontAwesomeIcon
                          icon={platformInfo.icon}
                          className="text-white text-base"
                        />
                      </span>
                    ) : (
                      <span className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400 text-sm">?</span>
                      </span>
                    )}
                  </div>

                  {/* Platform info */}
                  <div className="flex-1 min-w-0">
                    <Tooltip content={data.platformUsername || platformInfo?.label || t('unknown_platform', { defaultValue: 'Unknown Platform' })} side="bottom">
                      <h2 className="text-lg font-bold text-gray-900 truncate">
                        {data.platformUsername || platformInfo?.label || t('unknown_platform', { defaultValue: 'Unknown Platform' })}
                      </h2>
                    </Tooltip>
                    <p className="text-sm text-gray-500">
                      {platformInfo?.label} •{' '}
                      <Tooltip content={formatDateTime(data.publishedAt)} side="bottom">
                        <span className="cursor-help">{formatRelativeDate(data.publishedAt)}</span>
                      </Tooltip>
                    </p>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* External link button */}
                  {data.externalUrl && (
                    <Tooltip content={t('open_post', { defaultValue: 'View on platform' })} side="bottom">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(data.externalUrl!, '_blank', 'noopener,noreferrer');
                        }}
                        className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded-lg transition-colors"
                        aria-label={t('open_post', { defaultValue: 'View on platform' })}
                      >
                        <Icon name={ICONS.LINK} size="custom" className="inline-block text-[14px]" color="text-gray-500" ariaHidden />
                      </button>
                    </Tooltip>
                  )}

                  {/* Refresh button */}
                  <Tooltip
                    content={
                      isRefreshDisabled && refreshState === 'idle'
                        ? t('refresh_rate_limited', { defaultValue: 'Analytics were refreshed recently' })
                        : refreshState === 'waiting'
                          ? t('refresh_waiting', { defaultValue: 'Waiting for fresh data...' })
                          : t('refresh_analytics', { defaultValue: 'Refresh analytics' })
                    }
                    side="bottom"
                  >
                    <button
                      onClick={handleRefresh}
                      disabled={isRefreshDisabled || refreshState !== 'idle'}
                      className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label={t('refresh_analytics', { defaultValue: 'Refresh analytics' })}
                    >
                      <Icon
                        name={ICONS.REFRESH}
                        size="custom"
                        className={`inline-block text-[14px] ${refreshState === 'requesting' || refreshState === 'waiting' ? 'animate-spin' : ''}`}
                        color="text-gray-500"
                        ariaHidden
                      />
                    </button>
                  </Tooltip>

                  {/* Close button */}
                  <Tooltip content={t('close', { defaultValue: 'Close' })} side="bottom">
                    <button
                      onClick={handleClose}
                      className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded-lg transition-colors"
                      aria-label={t('close', { defaultValue: 'Close' })}
                    >
                      <Icon name={ICONS.CLOSE} size="custom" className="inline-block text-[12px]" color="text-gray-500" ariaHidden />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>

            {/* Channel Stats Bar - shows stats at time of publish */}
            {data.channelStats && (
              <div className="px-5 sm:px-4 py-2 border-b border-gray-100 flex items-center gap-3 flex-shrink-0">
                <span className="text-[10px] text-gray-400 italic whitespace-nowrap">
                  {t('analytics:at_publish', { defaultValue: 'At publish' })}
                  {data.channelStats.recordedAt && (
                    <> · {formatDateOnly(data.channelStats.recordedAt)}</>
                  )}
                </span>
                <div className="w-px h-3 bg-gray-200" />
                {data.channelStats.followerCount !== undefined && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-gray-400 uppercase tracking-wide">
                      {t('followers', { defaultValue: 'Followers' })}
                    </span>
                    <span className="text-xs font-semibold text-gray-700">
                      {formatCompactNumber(data.channelStats.followerCount)}
                    </span>
                  </div>
                )}
                {data.channelStats.postsCount !== null && data.channelStats.postsCount !== undefined && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-gray-400 uppercase tracking-wide">
                      {t('total_posts', { defaultValue: 'Posts' })}
                    </span>
                    <span className="text-xs font-semibold text-gray-700">
                      {formatCompactNumber(data.channelStats.postsCount)}
                    </span>
                  </div>
                )}
                {data.channelStats.likesCount !== null && data.channelStats.likesCount !== undefined && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-gray-400 uppercase tracking-wide">
                      {t('total_likes', { defaultValue: 'Likes' })}
                    </span>
                    <span className="text-xs font-semibold text-gray-700">
                      {formatCompactNumber(data.channelStats.likesCount)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-5 sm:px-4 py-4 space-y-6">
                {/* Post Content (CalendarPostPopover style) */}
                <div className="flex gap-3">
                  {/* Thumbnail */}
                  {data.postMedia && data.postMedia.length > 0 ? (
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 shadow-sm ring-1 ring-gray-100 relative">
                      <RetryImage
                        src={data.postMedia[0].thumbnailUrl || data.postMedia[0].originalUrl || undefined}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      {/* Post type overlay badge */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm px-1.5 py-0.5">
                        <span className="flex items-center gap-1 text-[10px] font-medium text-white">
                          <Icon name={typeIcon} size="xs" className="text-white" ariaHidden />
                          {typeLabel}
                        </span>
                      </div>
                      {/* Multiple media count badge */}
                      {data.postMedia.length > 1 && (
                        <div className="absolute top-1 right-1 bg-black/60 backdrop-blur-sm px-1 py-0.5 rounded text-[10px] font-medium text-white">
                          +{data.postMedia.length - 1}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 shadow-sm ring-1 ring-gray-100 bg-gray-100 flex items-center justify-center relative">
                      <Icon name={ICONS.EDIT} size="lg" className="text-gray-300" ariaHidden />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm px-1.5 py-0.5">
                        <span className="flex items-center gap-1 text-[10px] font-medium text-white">
                          <Icon name={typeIcon} size="xs" className="text-white" ariaHidden />
                          {typeLabel}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Caption with Read More */}
                  <div className="flex-1 min-w-0">
                    {caption ? (
                      <div className="space-y-1.5">
                        <p className={`text-sm text-gray-700 leading-relaxed break-words ${
                          !captionExpanded && shouldShowReadMore ? 'line-clamp-3' : 'whitespace-pre-wrap'
                        }`}>
                          {caption}
                        </p>
                        {shouldShowReadMore && (
                          <button
                            onClick={() => setCaptionExpanded(!captionExpanded)}
                            className="text-primary-600 text-xs font-medium cursor-pointer hover:text-primary-700 transition-colors"
                          >
                            {captionExpanded
                              ? t('read_less', { defaultValue: 'Read less' })
                              : t('read_more', { defaultValue: 'Read more' })}
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">{t('no_caption', { defaultValue: '(No caption)' })}</p>
                    )}
                  </div>
                </div>

                {/* Caption stats badges (below the flex row) */}
                {caption && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                      {charCount} {t('chars', { defaultValue: 'chars' })}
                    </span>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                      {wordCount} {t('words', { defaultValue: 'words' })}
                    </span>
                    {hashtags.length > 0 && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                        {hashtags.length} #
                      </span>
                    )}
                  </div>
                )}

                {/* Metrics Grid */}
                {latestSnapshot && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">
                      {t('current_metrics', { defaultValue: 'Current Metrics' })}
                    </h3>

                    {/* Platform-specific note */}
                    {analyticsConfig.noteKey && (
                      <div className="flex items-start gap-2 p-2.5 bg-primary-50 rounded-lg text-xs text-secondary-700 mb-3">
                        <i className="hm hm-info text-sm text-primary-500 flex-shrink-0 mt-0.5" />
                        <span>{t(analyticsConfig.noteKey, { defaultValue: analyticsConfig.noteDefault || '' })}</span>
                      </div>
                    )}

                    {/* Group A: Engagement Breakdown (always shown since engagement is calculated) */}
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <MetricTile
                          label={t('engagement', { defaultValue: 'Engagement' })}
                          value={formatCompactNumber(latestSnapshot.engagement)}
                        />
                        {isMetricSupported(platform, 'likes') && (
                          <MetricTile
                            label={t('likes', { defaultValue: 'Likes' })}
                            value={latestSnapshot.likes.toLocaleString()}
                          />
                        )}
                        {isMetricSupported(platform, 'comments') && (
                          <MetricTile
                            label={t('comments', { defaultValue: 'Comments' })}
                            value={latestSnapshot.comments.toLocaleString()}
                          />
                        )}
                        {isMetricSupported(platform, 'shares') && (
                          <MetricTile
                            label={t('shares', { defaultValue: 'Shares' })}
                            value={latestSnapshot.shares.toLocaleString()}
                          />
                        )}
                        {isMetricSupported(platform, 'saves') && (
                          <MetricTile
                            label={t('saves', { defaultValue: 'Saves' })}
                            value={latestSnapshot.saves.toLocaleString()}
                          />
                        )}
                      </div>

                      {/* Group B: Distribution (conditional on platform support) */}
                      {supportsDistributionMetrics(platform) && (
                        <>
                          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2">
                            {t('distribution', { defaultValue: 'Distribution' })}
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {isMetricSupported(platform, 'reach') && (
                              <MetricTile
                                label={t('reach', { defaultValue: 'Reach' })}
                                value={formatCompactNumber(latestSnapshot.reach)}
                              />
                            )}
                            {isMetricSupported(platform, 'impressions') && (
                              <MetricTile
                                label={t('impressions', { defaultValue: 'Impressions' })}
                                value={formatCompactNumber(latestSnapshot.impressions)}
                              />
                            )}
                            {isMetricSupported(platform, 'clicks') && (
                              <MetricTile
                                label={t('clicks', { defaultValue: 'Clicks' })}
                                value={latestSnapshot.clicks.toLocaleString()}
                              />
                            )}
                          </div>
                        </>
                      )}

                      {/* Group C: Video Metrics (conditional on both platform support AND data) */}
                      {supportsVideoMetrics(platform) &&
                        (latestSnapshot.videoViews !== null && latestSnapshot.videoViews > 0 ||
                         latestSnapshot.videoWatchTime !== null && latestSnapshot.videoWatchTime > 0 ||
                         latestSnapshot.videoCompletionRate !== null && latestSnapshot.videoCompletionRate > 0) && (
                        <>
                          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2">
                            {t('video_metrics', { defaultValue: 'Video Metrics' })}
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {isMetricSupported(platform, 'videoViews') && latestSnapshot.videoViews !== null && latestSnapshot.videoViews > 0 && (
                              <MetricTile
                                label={t('video_views', { defaultValue: 'Video Views' })}
                                value={formatCompactNumber(latestSnapshot.videoViews)}
                              />
                            )}
                            {isMetricSupported(platform, 'videoWatchTime') && latestSnapshot.videoWatchTime !== null && latestSnapshot.videoWatchTime > 0 && (
                              <MetricTile
                                label={t('watch_time', { defaultValue: 'Watch Time' })}
                                value={formatWatchTime(latestSnapshot.videoWatchTime)}
                              />
                            )}
                            {isMetricSupported(platform, 'videoCompletionRate') && latestSnapshot.videoCompletionRate !== null && latestSnapshot.videoCompletionRate > 0 && (
                              <MetricTile
                                label={t('completion_rate', { defaultValue: 'Completion Rate' })}
                                value={formatPercent(latestSnapshot.videoCompletionRate)}
                              />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Snapshot Timeline Chart */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">
                    {t('performance_over_time', { defaultValue: 'Performance Over Time' })}
                  </h3>
                  {allMetricsZero ? (
                    // Collecting data state
                    <div className="text-center py-10">
                      <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-4">
                        <i className="hm hm-graph text-2xl text-primary-400" />
                      </div>
                      <p className="text-sm font-medium text-gray-700">
                        {t('collecting_data', { defaultValue: 'Collecting data...' })}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {t('collecting_data_desc', {
                          defaultValue: 'Analytics snapshots are collected automatically. Check back soon!',
                        })}
                      </p>
                    </div>
                  ) : data.snapshots && data.snapshots.length > 0 ? (
                    <SnapshotChart snapshots={data.snapshots} publishedAt={data.publishedAt} platform={platform} />
                  ) : (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      <i className="hm hm-graph text-2xl mb-2 block" />
                      <p>
                        {t('more_data_points', {
                          defaultValue: 'More data points will appear as analytics are collected',
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};
